import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { client, queries } from '../lib/sanity';
import {
  generarExcelCatalogo, descargarWorkbook,
  parseExcelCatalogo, analizarCambios,
  TIPO_COMPLETO, TIPO_ANDROID,
  CATEGORIA_LABEL,
} from '../lib/excelRoundtrip';
import './AdminTasa.css';

const SHEETJS_CDN = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
const CONFIG_QUERY = '*[_id=="configuracion-principal"][0]{tasaCambio,tasaInterna}';

export default function AdminProductos() {
  const [sheetReady, setSheetReady] = useState(!!window.XLSX);
  const [productos, setProductos] = useState(null);
  const [tasas, setTasas] = useState({ interna: null, bcv: null });
  const [error, setError] = useState(null);

  const [stage, setStage] = useState('idle');
  const [analisis, setAnalisis] = useState(null);
  const [warningOcultar, setWarningOcultar] = useState(false);
  const [contexto, setContexto] = useState(null); // { tipo }
  const [password, setPassword] = useState('');
  const [applyMsg, setApplyMsg] = useState(null);

  useEffect(() => {
    if (window.XLSX) { setSheetReady(true); return; }
    const script = document.createElement('script');
    script.src = SHEETJS_CDN;
    script.onload = () => setSheetReady(true);
    script.onerror = () => setError('No se pudo cargar la librería de Excel');
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  useEffect(() => {
    client.fetch(queries.allProducts).then(setProductos).catch(err => {
      setError('Error cargando catálogo: ' + err.message);
    });
    client.fetch(CONFIG_QUERY).then(d => {
      setTasas({ interna: d?.tasaInterna || null, bcv: d?.tasaCambio || null });
    }).catch(() => {});
  }, []);

  const handleDescargar = (tipo) => {
    if (!productos || !sheetReady) return;
    try {
      const wb = generarExcelCatalogo(productos, { tipo, tasaInterna: tasas.interna, tasaBCV: tasas.bcv });
      const fecha = new Date().toISOString().slice(0, 10);
      const sufijo = tipo === TIPO_ANDROID ? '-android' : '-completo';
      descargarWorkbook(wb, `catalogo-nuovocell${sufijo}-${fecha}.xlsx`);
    } catch (e) {
      setError('Error generando Excel: ' + e.message);
    }
  };

  const handleFile = useCallback(async (file, tipo) => {
    if (!file) return;
    if (!window.XLSX) { setError('Librería de Excel no lista'); return; }
    if (!productos) { setError('Catálogo aún cargando, espera unos segundos'); return; }
    setError(null);
    setStage('parsing');
    setContexto({ tipo });

    try {
      const buf = await file.arrayBuffer();
      const wb = window.XLSX.read(buf, { type: 'array' });
      const p = parseExcelCatalogo(wb);
      if (p.errores.length > 0) {
        setError(p.errores.join('. '));
        setStage('idle');
        return;
      }

      // Validar coherencia: el tipo elegido debe matchear el tipo del Excel
      if (p.tipo !== tipo) {
        const elTipo = (t) => t === TIPO_ANDROID ? 'Android' : 'catálogo completo';
        setError(`Subiste un Excel de ${elTipo(p.tipo)} en el panel de ${elTipo(tipo)}. Por seguridad, súbelo al panel correcto.`);
        setStage('idle');
        return;
      }

      const a = analizarCambios(p.rows, productos, { tipo });
      setAnalisis(a);

      // Safeguard: > 50% de ocultar
      const universo = tipo === TIPO_ANDROID
        ? productos.filter(x => x.categoria === 'smartphones' && x.disponible !== false)
        : productos.filter(x => x.categoria !== 'smartphones' && x.disponible !== false);
      const ratio = a.faltantes.length / Math.max(universo.length, 1);
      setWarningOcultar(ratio >= 0.5);

      setStage('preview');
    } catch (e) {
      setError('Error parseando Excel: ' + e.message);
      setStage('idle');
    }
  }, [productos]);

  const onDrop = (tipo) => (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file, tipo);
  };
  const onPickFile = (tipo) => (e) => {
    const file = e.target.files[0];
    if (file) handleFile(file, tipo);
  };

  const handleApply = async () => {
    if (!password) { setApplyMsg({ type: 'error', text: 'Ingresa la contraseña' }); return; }
    if (!analisis) return;
    setStage('applying');
    setApplyMsg(null);

    const payload = {
      password,
      updatesFull: analisis.updates.map(u => ({ _id: u._id, sets: u.sets, unsets: u.unsets })),
      createsFull: analisis.creates.map(c => ({ doc: c.doc })),
      hideIds: analisis.faltantes.map(f => f._id),
    };

    try {
      const res = await fetch('/api/admin/apply-costos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        const parts = [];
        if (data.updated) parts.push(`${data.updated} actualizados`);
        if (data.created) parts.push(`${data.created} creados`);
        if (data.hidden)  parts.push(`${data.hidden} ocultados`);
        setApplyMsg({ type: 'success', text: parts.join(' · ') || 'Cambios aplicados' });
        setPassword('');
        setStage('done');
      } else {
        setApplyMsg({ type: 'error', text: data.error || 'Error al aplicar' });
        setStage('preview');
      }
    } catch (e) {
      setApplyMsg({ type: 'error', text: 'Error de red: ' + e.message });
      setStage('preview');
    }
  };

  const handleReset = () => {
    setStage('idle');
    setAnalisis(null);
    setWarningOcultar(false);
    setContexto(null);
    setApplyMsg(null);
  };

  // Conteos
  const productosCompleto = productos ? productos.filter(p => p.categoria !== 'smartphones').length : 0;
  const productosAndroid  = productos ? productos.filter(p => p.categoria === 'smartphones').length : 0;

  return (
    <div className="admin-tasa">
      <div className="admin-tasa__card" style={{ maxWidth: 920 }}>
        <div className="admin-tasa__logo">
          <img src="/logos/nuovocell-logo.png" alt="Nuovocell" />
        </div>
        <h1 className="admin-tasa__title">Catálogo de productos</h1>
        <p style={{ textAlign: 'center', fontSize: 13, color: '#666', marginBottom: 20 }}>
          <Link to="/admin/tasa">Tasa BCV</Link> · Catálogo
        </p>

        {error && <div className="admin-tasa__msg admin-tasa__msg--error">{error}</div>}

        {stage === 'idle' && (
          <>
            <div style={{ background: '#F7F9FB', borderRadius: 8, padding: 16, marginBottom: 20, fontSize: 13, lineHeight: 1.5, color: '#444' }}>
              <strong style={{ color: '#222' }}>Cómo funciona:</strong> hay dos Excel diferentes según el tipo de producto, cada uno con su propia fórmula de precio. Descarga, edita y sube.
            </div>

            <Tarjeta
              titulo="Catálogo completo"
              descripcion="Todos los productos EXCEPTO Android (iPhone, Laptops, Tablets, Accesorios, Internet, etc.)"
              formula="Precio = PVP TIENDA × 1.14 × INTERNA / BCV"
              conteo={productosCompleto}
              disabled={!productos || !sheetReady}
              onDescargar={() => handleDescargar(TIPO_COMPLETO)}
              onDrop={onDrop(TIPO_COMPLETO)}
              onPickFile={onPickFile(TIPO_COMPLETO)}
              inputId="dz-completo"
              color="azul"
            />

            <div style={{ height: 12 }} />

            <Tarjeta
              titulo="Android"
              descripcion="Solo Smartphones Android (sin iPhone)"
              formula="Precio = (si COSTO < $100: ×1.44, si no: ×1.34) × INTERNA / BCV"
              conteo={productosAndroid}
              disabled={!productos || !sheetReady}
              onDescargar={() => handleDescargar(TIPO_ANDROID)}
              onDrop={onDrop(TIPO_ANDROID)}
              onPickFile={onPickFile(TIPO_ANDROID)}
              inputId="dz-android"
              color="verde"
            />

            {!sheetReady && <p style={hintStyle}>Cargando librería de Excel...</p>}

            {tasas.bcv && tasas.interna && (
              <p style={{ ...hintStyle, marginTop: 20 }}>
                Tasas actuales — INTERNA: Bs. {tasas.interna} · BCV: Bs. {tasas.bcv}
              </p>
            )}
          </>
        )}

        {stage === 'parsing' && (
          <p style={{ textAlign: 'center', padding: '40px 0' }}>Procesando Excel...</p>
        )}

        {stage === 'preview' && analisis && (
          <>
            <div style={{ background: '#E6F1FB', color: '#0C447C', padding: '8px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
              Subida de: <strong>{contexto?.tipo === TIPO_ANDROID ? 'Android' : 'Catálogo completo'}</strong>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
              <Stat label="A actualizar" valor={analisis.updates.length} color="amber" />
              <Stat label="A crear"      valor={analisis.creates.length} color="green" />
              <Stat label="A ocultar"    valor={analisis.faltantes.length} color={warningOcultar ? 'red' : 'gray'} />
              <Stat label="Errores"      valor={analisis.errores.length} color={analisis.errores.length > 0 ? 'red' : 'gray'} />
            </div>

            {warningOcultar && (
              <div style={{
                background: '#FEEBE6', border: '1px solid #F4B4A1', color: '#882214',
                padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 13
              }}>
                ⚠️ <strong>Atención:</strong> el Excel tiene menos de la mitad de productos respecto al universo actual.
                Se van a ocultar <strong>{analisis.faltantes.length}</strong> productos. Revisa antes de aplicar.
              </div>
            )}

            {analisis.errores.length > 0 && (
              <Section title={`Errores (${analisis.errores.length})`} bad>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                  {analisis.errores.map((e, i) => (
                    <li key={i}>{e.fila > 0 ? `Fila ${e.fila}: ` : ''}{e.msg}</li>
                  ))}
                </ul>
              </Section>
            )}

            {analisis.updates.length > 0 && (
              <Section title={`Productos a actualizar (${analisis.updates.length})`}>
                <table style={tableStyle}>
                  <thead><tr>
                    <th style={tdLeft}>Producto</th>
                    <th style={tdLeft}>Cambios</th>
                    <th style={tdRight}>Precio nuevo</th>
                  </tr></thead>
                  <tbody>
                    {analisis.updates.slice(0, 100).map(u => (
                      <tr key={u._id}>
                        <td style={tdLeft}>{u.nombreActual}</td>
                        <td style={{ ...tdLeft, fontSize: 12, color: '#666' }}>{Object.keys(u.cambios).join(', ')}</td>
                        <td style={tdRight}>{u.sets.precio != null ? `$${u.sets.precio.toFixed(2)}` : '—'}</td>
                      </tr>
                    ))}
                    {analisis.updates.length > 100 && (
                      <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888', fontSize: 12, padding: 8 }}>
                        ...y {analisis.updates.length - 100} más
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </Section>
            )}

            {analisis.creates.length > 0 && (
              <Section title={`Productos nuevos a crear (${analisis.creates.length})`}>
                <table style={tableStyle}>
                  <thead><tr>
                    <th style={tdLeft}>Nombre</th>
                    <th style={tdLeft}>Categoría</th>
                    <th style={tdRight}>Precio</th>
                  </tr></thead>
                  <tbody>
                    {analisis.creates.slice(0, 100).map((c, i) => (
                      <tr key={i}>
                        <td style={tdLeft}>{c.doc.nombre}</td>
                        <td style={{ ...tdLeft, fontSize: 12 }}>{CATEGORIA_LABEL[c.doc.categoria] || c.doc.categoria}</td>
                        <td style={tdRight}>{c.doc.precio != null ? `$${c.doc.precio.toFixed(2)}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            )}

            {analisis.faltantes.length > 0 && (
              <Section title={`Productos que se ocultarán (${analisis.faltantes.length})`}>
                <p style={{ fontSize: 12, color: '#888', margin: '0 0 8px' }}>
                  Están en el catálogo pero no en el Excel. Quedarán ocultos (no se borran).
                </p>
                <div style={{ maxHeight: 180, overflowY: 'auto', fontSize: 13 }}>
                  {analisis.faltantes.map(f => (
                    <div key={f._id} style={{ padding: '2px 0', color: '#555' }}>• {f.nombre}</div>
                  ))}
                </div>
              </Section>
            )}

            <div style={{ marginTop: 20, padding: 16, background: '#f5f7fa', borderRadius: 8 }}>
              <div className="admin-tasa__group" style={{ marginBottom: 12 }}>
                <label>Contraseña para confirmar</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Contraseña de administrador" />
              </div>
              {applyMsg && <div className={`admin-tasa__msg admin-tasa__msg--${applyMsg.type}`}>{applyMsg.text}</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleReset}
                  style={{ flex: 1, padding: '10px 20px', borderRadius: 6, border: '1px solid #ccd', background: 'white', cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button onClick={handleApply} className="admin-tasa__btn" style={{ flex: 2 }}
                  disabled={analisis.updates.length + analisis.creates.length + analisis.faltantes.length === 0}>
                  Aplicar cambios
                </button>
              </div>
            </div>
          </>
        )}

        {stage === 'applying' && (
          <p style={{ textAlign: 'center', padding: '40px 0' }}>Aplicando cambios...</p>
        )}

        {stage === 'done' && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            {applyMsg && <div className={`admin-tasa__msg admin-tasa__msg--${applyMsg.type}`}>{applyMsg.text}</div>}
            <p style={{ fontSize: 13, color: '#666', marginTop: 16 }}>
              Los cambios ya están en la web (puede tardar hasta 1 minuto en propagarse).
            </p>
            <button onClick={() => window.location.reload()} className="admin-tasa__btn" style={{ marginTop: 12 }}>
              Volver al inicio
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============== sub-componentes ==============

function Tarjeta({ titulo, descripcion, formula, conteo, disabled, onDescargar, onDrop, onPickFile, inputId, color }) {
  const bg = color === 'verde' ? '#EAF3DE' : '#E6F1FB';
  const fg = color === 'verde' ? '#3B6D11' : '#0C447C';
  const btn = color === 'verde' ? '#3B6D11' : '#1a73e8';

  return (
    <div style={{
      border: '1px solid ' + bg,
      background: bg,
      borderRadius: 10, padding: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: fg }}>{titulo}</div>
        <div style={{ fontSize: 12, color: fg, opacity: 0.8 }}>{conteo} productos</div>
      </div>
      <div style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>{descripcion}</div>
      <div style={{ fontSize: 11, color: '#666', fontFamily: 'monospace', marginBottom: 12 }}>{formula}</div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onDescargar} disabled={disabled || conteo === 0}
          style={{
            flex: 1, padding: '10px 14px', borderRadius: 6,
            border: 'none', background: btn, color: 'white',
            fontFamily: 'inherit', fontSize: 13, cursor: disabled ? 'default' : 'pointer',
            opacity: (disabled || conteo === 0) ? 0.4 : 1,
          }}>
          📥 Descargar
        </button>
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => document.getElementById(inputId).click()}
          style={{
            flex: 1, border: '1px dashed #aaa', borderRadius: 6,
            padding: '8px 12px', cursor: 'pointer', background: 'rgba(255,255,255,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, color: '#444',
          }}>
          📤 Subir editado
          <input id={inputId} type="file" accept=".xlsx,.xls"
            onChange={onPickFile} style={{ display: 'none' }} />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, valor, color }) {
  const palette = {
    green:  { bg: '#EAF3DE', fg: '#3B6D11' },
    amber:  { bg: '#FAEEDA', fg: '#854F0B' },
    red:    { bg: '#FEEBE6', fg: '#882214' },
    gray:   { bg: '#f5f7fa', fg: '#555' },
  };
  const { bg, fg } = palette[color] || palette.gray;
  return (
    <div style={{ background: bg, color: fg, padding: 12, borderRadius: 6, textAlign: 'center' }}>
      <div style={{ fontSize: 11, opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 500 }}>{valor}</div>
    </div>
  );
}

function Section({ title, children, bad }) {
  return (
    <div style={{
      marginTop: 16,
      border: '1px solid ' + (bad ? '#F4B4A1' : '#e3e8ee'),
      borderRadius: 8, padding: 14,
      background: bad ? '#FFFBFA' : 'white',
    }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 500, color: bad ? '#882214' : '#222' }}>{title}</h3>
      {children}
    </div>
  );
}

const tableStyle = { width: '100%', fontSize: 13, borderCollapse: 'collapse' };
const tdLeft = { textAlign: 'left', padding: '4px 6px' };
const tdRight = { textAlign: 'right', padding: '4px 6px' };
const hintStyle = { fontSize: 12, color: '#999', textAlign: 'center', marginTop: 12 };
